import { useEffect } from 'react';
import { createSequence, queryString } from '@olivierpascal/helpers';
import { Button, InputChip, Stack, Text } from '@sixui/ui';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import type { IContainerProps } from '~/components/utils/Container';
import type { IAppRegion } from '~/lib/appSettings';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { ErrorMessage } from '~/components/molecules/ErrorMessage';
import { Session, SessionSkeleton } from '~/components/molecules/Session';
import { Table } from '~/components/molecules/Table';
import { useNavigate } from '~/components/utils/Navigation';
import { appRegionsSettings } from '~/lib/appSettings';
import {
  allCredentialsQueryKeyFactory,
  useAllCredentialsQuery,
  useAuth,
} from '~/lib/auth';

const GRID_TEMPLATE_COLUMNS = '1fr min-content minmax(140px, min-content)';
const DEFAULT_SKELETON_COUNT = 1;

export type ISessionsProps = IContainerProps & {
  appRegion?: string;
  previousUrl?: string;
  nextUrl?: string;
};

export const Sessions: React.FC<ISessionsProps> = (props) => {
  const { sx, appRegion, nextUrl } = props;
  const { t } = useTranslation();
  const auth = useAuth();
  const allCredentialsQuery = useAllCredentialsQuery();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEmpty =
    !allCredentialsQuery.data?.length && !allCredentialsQuery.isLoading;

  useEffect(() => {
    if (isEmpty) {
      navigate('^/login');
    }
  }, [isEmpty, navigate]);

  const handleCloseSession = (sessionId: string): Promise<void> =>
    auth.logOut({ sessionIds: [sessionId], openUrl: false }).then(() =>
      queryClient.invalidateQueries({
        queryKey: allCredentialsQueryKeyFactory(),
      }),
    );

  const credentials = allCredentialsQuery.data?.filter(
    (credentials) => !appRegion || credentials.region === appRegion,
  );

  return (
    <Stack sx={sx} gap={2}>
      {appRegion && (
        <Stack horizontal gap={2}>
          <Text variant="label" size="lg">
            {t('Sessions.filters')}
          </Text>
          <InputChip
            label={t('Sessions.appRegion', {
              appRegion:
                appRegionsSettings[appRegion as IAppRegion]?.name ?? appRegion,
            })}
            onDelete={() =>
              navigate(
                `^/sessions/?${queryString.build({
                  nextUrl,
                })}`,
              )
            }
          />
        </Stack>
      )}
      <ContentBlock>
        {allCredentialsQuery.isError ? (
          <ContentBlock.Body first>
            <ErrorMessage error={allCredentialsQuery.error} />
          </ContentBlock.Body>
        ) : isEmpty ? (
          <ContentBlock.Empty title={t('Sessions.empty')}>
            <Button href="^/login">{t('Sessions.action')}</Button>
          </ContentBlock.Empty>
        ) : allCredentialsQuery.isLoading ? (
          <Table gridTemplateColumns={GRID_TEMPLATE_COLUMNS}>
            {createSequence(DEFAULT_SKELETON_COUNT).map((index) => (
              <SessionSkeleton key={index} first={index === 0} />
            ))}
          </Table>
        ) : credentials?.length ? (
          <Table gridTemplateColumns={GRID_TEMPLATE_COLUMNS}>
            {credentials.map((credentials, credentialsIndex) => (
              <Session
                key={credentials.sessionId}
                credentials={credentials}
                nextUrl={nextUrl}
                onClose={() => handleCloseSession(credentials.sessionId)}
                first={credentialsIndex === 0}
              />
            ))}
          </Table>
        ) : (
          <ContentBlock.Empty title={t('Sessions.empty')} />
        )}
      </ContentBlock>
    </Stack>
  );
};
